/**
 * PermitSection (PERMIT-01, Seth 2026-09-24) — the job's Permits tab.
 *
 *   "they should be able to request permits to be pulled. Similar feel to how
 *    it will be done for installs - office should also be able to add permit
 *    info, etc. Sometimes permits are done by the new home builder and we
 *    don't need to pull them so not every job might need a permit."
 *
 *   · WHO PULLS IT — one answer per job: We pull it · Builder pulls it · Not
 *     needed. Installs start at "We pull it"; a new-construction house asks.
 *   · A permit moves Requested → Applied → Issued → Finaled (or Cancelled).
 *     Requested = the crew (or office) asked and nobody has pulled it yet —
 *     it sits on top here and in the permit desk's queue.
 *   · Inspections (rough-in / final / other) hang off each permit; a passed
 *     final finals the permit.
 *   · The builder's own permit can be recorded for reference (the crew sees
 *     the inspection status) — never billed, never counted as ours.
 *
 * Competitor frame: ServiceTitan has no real permit record (tags + a
 * coordinator spreadsheet); builders pull their own on most production
 * homes ("permit by others"). One record, one queue, same feel for installs
 * and new construction.
 *
 * FILES-READ-02 (2026-09-28): upload the permit paperwork (here, or anywhere
 * on the job) and its number, city, fee and status are read off it — Fill It
 * In puts them in the form to check, the same way the AHRI certificate works.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle, CalendarClock, Check, ClipboardCheck, HardHat, Loader2, Pencil, Plus, ShieldCheck, Sparkles, Trash2, Upload, X,
} from "lucide-react";
import { toastError } from "@/components/ui/mutationToast";
import { useJobFiles, useUploadFiles, type FileAiData } from "@/features/files/filesCore";
import { cn } from "@/lib/cn";
import { Button } from "@/components/ui/Button";
import { useOrgSettingJson } from "@/lib/orgSettings";
import {
  type Job, type JobPermit, type PermitBy, type PermitStatus, type PermitInspection,
  useJobPermits, useAddPermit, useDeletePermit, useUpdatePermit, useJurisdictions, useSetPermitBy,
  usePermitInspections, useAddInspection, useUpdateInspection, useDeleteInspection,
  effectivePermitBy, isNCJob, permitIsOnFile,
  PERMIT_STATUSES, PERMIT_STATUS_LABEL, PERMIT_BY_LABEL, INSPECTION_KIND_LABEL, INSPECTION_RESULT_LABEL,
} from "./jobs";
import { usePostInstall } from "@/features/dispatch-detail/postInstall";

const money = (n: number | null | undefined) =>
  n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const fieldCls = "w-full rounded-md border border-hairline bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30";
const selCls = "rounded-md border border-hairline bg-white px-2 py-1 text-xs font-semibold text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30";

/** Permit types for the picker + the one-click "installs usually need" chips.
 *  Admin-editable: Settings → Job Documents & Permits. */
export interface PermitType { label: string; suggest: boolean }
export const PERMIT_TYPES_DEFAULT: PermitType[] = [
  { label: "Mechanical", suggest: true },
  { label: "Electrical", suggest: true },
  { label: "Plumbing", suggest: false },
  { label: "Gas", suggest: false },
  { label: "Mechanical Warm Air Permit", suggest: false },
];
export function usePermitTypes() {
  const q = useOrgSettingJson<PermitType[]>("permit_types", PERMIT_TYPES_DEFAULT);
  const list = Array.isArray(q.data) && q.data.length ? q.data.filter((t) => t && typeof t.label === "string") : PERMIT_TYPES_DEFAULT;
  return list;
}

/** Customer-invoice permit label: "Mechanical Permit", or a custom string verbatim. */
export function permitLabel(trade: string | null): string {
  const t = (trade ?? "").trim();
  if (!t) return "Mechanical Permit";
  const cap = t.charAt(0).toUpperCase() + t.slice(1);
  return /permit/i.test(cap) ? cap : cap + " Permit";
}

/** "terry@x.com" → "terry"; a plain name stays a name. */
export function whoLabel(s: string | null | undefined): string {
  const v = (s ?? "").trim();
  if (!v) return "someone";
  return v.includes("@") ? v.split("@")[0] : v;
}
export function whenLabel(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  if (sameDay) return `today ${time}`;
  const days = Math.round((today.getTime() - d.getTime()) / 86400000);
  if (days < 7 && days >= 0) return `${d.toLocaleDateString("en-US", { weekday: "short" })} ${time}`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
const fmtDay = (iso: string | null) =>
  iso ? new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "no date yet";

export const PERMIT_STATUS_TONE: Record<PermitStatus, string> = {
  requested: "bg-amber-50 text-amber-800 ring-amber-200",
  applied: "bg-sky-50 text-sky-700 ring-sky-200",
  issued: "bg-brand-50 text-brand-700 ring-brand-200",
  finaled: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  cancelled: "bg-slate-100 text-slate-500 ring-slate-200",
};
const RESULT_TONE: Record<PermitInspection["result"], string> = {
  needed: "text-amber-700", scheduled: "text-sky-700", passed: "text-emerald-700", failed: "text-rose-600", cancelled: "text-slate-400",
};

/** What a permit form starts with — typed, or read off the paperwork. */
interface PermitDraft { trade?: string | null; permit_no?: string | null; jurisdiction?: string | null; fee?: number | null; status?: PermitStatus; notes?: string | null }
type PermitRead = NonNullable<FileAiData["permit"]>;
const normNo = (s: string | null | undefined) => (s ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");

function draftFromRead(r: PermitRead, types: PermitType[], jurisdictions: { name: string }[]): PermitDraft {
  const tr = (r.trade ?? "").toLowerCase();
  const trade = tr ? (types.find((t) => t.label.toLowerCase() === tr)?.label ?? tr.charAt(0).toUpperCase() + tr.slice(1)) : null;
  const jur = r.jurisdiction ? (jurisdictions.find((j) => j.name.toLowerCase() === r.jurisdiction!.toLowerCase())?.name ?? r.jurisdiction) : null;
  const expires = r.expires ? new Date(`${r.expires}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null;
  return {
    trade, permit_no: r.permit_no, jurisdiction: jur, fee: r.fee,
    status: r.status === "finaled" ? "finaled" : r.status === "issued" || r.issued ? "issued" : "applied",
    notes: [r.work, expires ? `Expires ${expires}` : null].filter(Boolean).join(" · ") || null,
  };
}

export function PermitSection({ job, closed }: { job: Job; closed: boolean }) {
  const { data: permits } = useJobPermits(job.id);
  const { data: inspections } = usePermitInspections(job.id);
  const { data: postInstall } = usePostInstall({ dispatch: job.source_dispatch ?? null, jobId: job.id });
  const { data: files } = useJobFiles(job.id);
  const { data: jurisdictions } = useJurisdictions();
  const setBy = useSetPermitBy();
  const by = effectivePermitBy(job, postInstall ?? null);
  const nc = isNCJob(job);
  const types = usePermitTypes();
  const upload = useUploadFiles({ custno: job.custno, locno: job.locno ?? null, job_id: job.id, doc_tag: "permit_doc", origin: "office", sort: true });
  const pickRef = useRef<HTMLInputElement>(null);
  const [addDraft, setAddDraft] = useState<{ key: string; d: PermitDraft } | null>(null);
  const [reqFill, setReqFill] = useState<{ id: string; d: PermitDraft } | null>(null);

  const list = permits ?? [];
  const requested = list.filter((p) => p.status === "requested" && !p.by_builder);
  const others = list.filter((p) => !(p.status === "requested" && !p.by_builder));
  const onFile = list.filter(permitIsOnFile).length;
  // FILES-READ-02: permit paperwork the AI has read whose number isn't on the job yet.
  const permitDocs = (files ?? []).filter((f) => f.doc_tag === "permit_doc");
  const known = new Set(list.map((p) => normNo(p.permit_no)).filter(Boolean));
  const reads = permitDocs
    .map((f) => ({ f, r: (f.ai_data as FileAiData | null | undefined)?.permit ?? null }))
    .filter((x): x is { f: typeof x.f; r: PermitRead } => !!x.r && (!x.r.permit_no || !known.has(normNo(x.r.permit_no))))
    .filter((x, i, all) => !x.r.permit_no || all.findIndex((y) => normNo(y.r.permit_no) === normNo(x.r.permit_no)) === i);
  const reading = upload.isPending || permitDocs.some((f) => !f.ai_at && Date.now() - new Date(f.created_at).getTime() < 3 * 60 * 1000);
  const fillIn = (r: PermitRead) => {
    const d = draftFromRead(r, types, jurisdictions ?? []);
    if (requested.length > 0) setReqFill({ id: requested[0].id, d });
    else setAddDraft({ key: `${Date.now()}`, d });
  };

  const pick = (v: PermitBy) => {
    if (v === by || closed) return;
    setBy.mutate({ job, permitBy: v });
  };

  return (
    <section className="rounded-xl border border-hairline bg-white p-4">
      <h3 className="mb-3 flex items-center gap-2 text-base font-semibold text-slate-800">
        <ShieldCheck className="h-[18px] w-[18px] text-brand-500" /> Permits
        <span className="ml-auto text-xs font-medium text-slate-400">
          {by === "builder" ? "Builder pulls it" : by === "none" ? "Not needed" : onFile ? `${onFile} on file` : requested.length ? "Requested — not pulled yet" : by === "sedgwick" ? "None on file yet" : "Who pulls it?"}
        </span>
        {!closed && by !== "none" && (
          <>
            <button type="button" onClick={() => pickRef.current?.click()} disabled={upload.isPending}
              title="Upload the permit — its number, city and fee are read off it"
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-50 px-3 py-1.5 text-sm font-semibold text-brand-700 transition-colors hover:bg-brand-100 disabled:opacity-60">
              {upload.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload the Permit
            </button>
            <input ref={pickRef} type="file" accept=".pdf,image/*" multiple className="hidden" aria-label="Upload permit paperwork"
              onChange={(e) => {
                const l = e.target.files ? [...e.target.files] : [];
                e.target.value = "";
                if (l.length) upload.mutate(l, { onError: (er) => toastError(er instanceof Error ? `Upload failed: ${er.message}` : "Upload failed.") });
              }} />
          </>
        )}
      </h3>

      {/* FILES-READ-02: what the permit paperwork says, one click into the form. */}
      {!closed && by !== "none" && reads.map(({ f, r }) => (
        <div key={f.id} className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-violet-200 bg-violet-50/70 px-3 py-2.5 text-sm">
          <Sparkles className="h-4 w-4 shrink-0 text-violet-600" />
          <span className="min-w-0 flex-1 text-slate-700">
            The permit paperwork reads{" "}
            <b className="font-semibold text-slate-900">{[r.trade ? permitLabel(r.trade) : "Permit", r.permit_no ? `#${r.permit_no}` : null].filter(Boolean).join(" ")}</b>
            {[r.jurisdiction, r.issued ? `issued ${new Date(`${r.issued}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : null, r.fee != null ? money(r.fee) : null]
              .filter(Boolean).map((x) => ` · ${x}`).join("")}
          </span>
          <Button size="sm" onClick={() => fillIn(r)}><Check className="h-4 w-4" /> Fill It In</Button>
        </div>
      ))}
      {!closed && reading && reads.length === 0 && (
        <p className="mb-3 inline-flex items-center gap-1.5 text-sm text-violet-700"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading the permit…</p>
      )}

      {/* WHO PULLS IT */}
      <div className="rounded-lg border border-hairline bg-slate-50/60 p-3">
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Who pulls the permit?</div>
        <div className="inline-flex flex-wrap gap-1 rounded-lg bg-white p-1 ring-1 ring-inset ring-slate-200" role="radiogroup" aria-label="Who pulls the permit">
          {(["sedgwick", "builder", "none"] as PermitBy[]).map((v) => (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={by === v}
              disabled={closed || setBy.isPending}
              onClick={() => pick(v)}
              className={cn(
                "rounded-md px-3.5 py-1.5 text-[13px] font-semibold transition-colors disabled:cursor-not-allowed",
                by === v ? "bg-brand-700 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100",
              )}
            >
              {PERMIT_BY_LABEL[v]}
            </button>
          ))}
        </div>
        <p className="mt-2 text-xs leading-relaxed text-slate-500">
          {by === "sedgwick" && "We pull it. The job stays in the permit queue until one of ours is on file (Applied or later)."}
          {by === "builder" && "The builder pulls it. No permit queue, nothing on our invoice, and it doesn't block closing. Record their permit # below so the crew can see the inspection status."}
          {by === "none" && "No permit for this job. It won't sit in the permit queue, and permit info won't go to the customer."}
          {by === null && (nc
            ? "New construction: pick who pulls the permit. Many builders pull their own."
            : "Pick who pulls the permit.")}
        </p>
        {setBy.isError && <p className="mt-1 text-[11px] font-medium text-rose-600">Couldn&apos;t save — try again.</p>}
      </div>

      {/* REQUESTED — the crew asked; nobody has pulled it yet. */}
      {requested.length > 0 && (
        <div className="mt-3 space-y-2">
          {requested.map((p) => (
            <RequestedRow key={`${p.id}-${reqFill?.id === p.id ? "fill" : ""}`} p={p} job={job} closed={closed}
              fill={reqFill?.id === p.id ? reqFill.d : null} onFilled={() => setReqFill(null)} />
          ))}
        </div>
      )}

      {/* PERMITS ON THE JOB */}
      {others.length > 0 && (
        <div className="mt-3 space-y-2">
          {others.map((p) => (
            <PermitRow key={p.id} p={p} closed={closed} inspections={(inspections ?? []).filter((i) => i.permit_id === p.id)} jobId={job.id} />
          ))}
        </div>
      )}

      {list.length === 0 && by === "sedgwick" && (
        <p className="mt-3 rounded-lg border border-dashed border-hairline py-5 text-center text-xs text-slate-400">No permits on this job yet.</p>
      )}

      {!closed && by !== "none" && by !== null && (
        <AddPermitForm key={addDraft?.key ?? "blank"} job={job} builder={by === "builder"} permits={list} types={types}
          initial={addDraft?.d ?? null} onAdded={() => setAddDraft(null)} />
      )}
    </section>
  );
}

/* ------------------------------------------------------------ requested */

function RequestedRow({ p, job, closed, fill = null, onFilled }: { p: JobPermit; job: Job; closed: boolean; fill?: PermitDraft | null; onFilled?: () => void }) {
  const [open, setOpen] = useState(!!fill);
  const del = useDeletePermit();
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/70 p-3">
      <div className="flex flex-wrap items-start gap-2">
        <HardHat className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        <div className="min-w-0 flex-1 text-sm">
          <div className="font-semibold text-amber-900">
            {permitLabel(p.trade)} requested by {whoLabel(p.requested_by)}
            <span className="ml-1.5 text-xs font-medium text-amber-700">{whenLabel(p.requested_at ?? p.created_at)}</span>
          </div>
          {p.request_note && <div className="mt-0.5 text-[13px] text-amber-800">&ldquo;{p.request_note}&rdquo;</div>}
          <div className="mt-0.5 text-xs text-amber-700">Not pulled yet.</div>
        </div>
        {!closed && !open && (
          <div className="flex shrink-0 items-center gap-1">
            <Button size="sm" onClick={() => setOpen(true)}><Check className="h-3.5 w-3.5" /> Fill In Permit Info</Button>
            <button type="button" onClick={() => del.mutate(p.id)} title="Remove the request" className="rounded p-1.5 text-amber-400 transition-colors hover:bg-amber-100 hover:text-rose-500">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
      {open && <PermitEditForm p={p} job={job} presetStatus={fill?.status ?? "applied"} initial={fill} onDone={() => { setOpen(false); onFilled?.(); }} />}
    </div>
  );
}

/* ---------------------------------------------------------------- a row */

function PermitRow({ p, closed, inspections, jobId }: { p: JobPermit; closed: boolean; inspections: PermitInspection[]; jobId: string }) {
  const [edit, setEdit] = useState(false);
  const [addInsp, setAddInsp] = useState(false);
  const del = useDeletePermit();
  const upd = useUpdatePermit();
  const updI = useUpdateInspection();
  const delI = useDeleteInspection();
  return (
    <div className={cn("rounded-lg border px-3 py-2.5 text-sm", p.status === "cancelled" ? "border-hairline bg-slate-50/40 opacity-70" : "border-hairline bg-slate-50/60")}>
      {edit ? (
        <PermitEditForm p={p} onDone={() => setEdit(false)} />
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <span className="min-w-0 flex-1 text-slate-700">
            <span className="font-semibold text-slate-800">{permitLabel(p.trade)}{p.permit_no ? ` #${p.permit_no}` : ""}</span>
            {p.jurisdiction ? ` · ${p.jurisdiction}` : ""}
            {p.fee != null && !p.by_builder ? <span className="text-slate-500"> · {money(p.fee)}</span> : null}
            {p.by_builder && <span className="ml-2 rounded-full bg-slate-200 px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide text-slate-600">Builder&apos;s permit</span>}
            {p.source === "powerhouse" && <span className="ml-2 text-[11px] text-slate-400">from the electrician&apos;s invoice</span>}
          </span>
          {closed ? (
            <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-bold ring-1 ring-inset", PERMIT_STATUS_TONE[p.status])}>{PERMIT_STATUS_LABEL[p.status]}</span>
          ) : (
            <select
              value={p.status}
              aria-label="Permit status"
              disabled={upd.isPending}
              onChange={(e) => upd.mutate({ id: p.id, status: e.target.value as PermitStatus, prevStatus: p.status })}
              className={cn(selCls, "ring-1 ring-inset", PERMIT_STATUS_TONE[p.status])}
            >
              {PERMIT_STATUSES.map((s) => <option key={s} value={s}>{PERMIT_STATUS_LABEL[s]}</option>)}
            </select>
          )}
          {!closed && (
            <div className="flex shrink-0 items-center gap-0.5">
              <button type="button" onClick={() => setEdit(true)} title="Edit permit #, city, fee" className="rounded p-1 text-slate-300 transition-colors hover:bg-slate-100 hover:text-slate-600"><Pencil className="h-3.5 w-3.5" /></button>
              <button type="button" onClick={() => del.mutate(p.id)} title="Remove permit" className="rounded p-1 text-slate-300 transition-colors hover:bg-rose-50 hover:text-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          )}
        </div>
      )}
      {(p.issued_at || p.finaled_at || p.requested_by || p.notes) && !edit && (
        <div className="mt-1 text-[11px] text-slate-400">
          {[
            p.requested_by ? `requested by ${whoLabel(p.requested_by)} ${whenLabel(p.requested_at)}` : null,
            p.pulled_by && !p.by_builder ? `pulled by ${whoLabel(p.pulled_by)}` : null,
            p.issued_at ? `issued ${whenLabel(p.issued_at)}` : null,
            p.finaled_at ? `finaled ${whenLabel(p.finaled_at)}` : null,
          ].filter(Boolean).join(" · ")}
          {p.notes ? <span className="block text-slate-500">{p.notes}</span> : null}
        </div>
      )}

      {/* INSPECTIONS */}
      {(inspections.length > 0 || (!closed && p.status !== "cancelled")) && (
        <div className="mt-2 border-t border-hairline pt-2">
          {inspections.map((i) => (
            <div key={i.id} className="group flex flex-wrap items-center gap-2 py-0.5 text-[13px]">
              <ClipboardCheck className="h-3.5 w-3.5 shrink-0 text-slate-400" />
              <span className="font-medium text-slate-700">{INSPECTION_KIND_LABEL[i.kind]} inspection</span>
              <span className="text-slate-500">· {fmtDay(i.scheduled_date)}</span>
              {closed ? (
                <span className={cn("text-xs font-semibold", RESULT_TONE[i.result])}>{INSPECTION_RESULT_LABEL[i.result]}</span>
              ) : (
                <select value={i.result} aria-label="Inspection result" onChange={(e) => updI.mutate({ id: i.id, permit_id: p.id, kind: i.kind, result: e.target.value as PermitInspection["result"] })}
                  className={cn(selCls, RESULT_TONE[i.result])}>
                  {(Object.keys(INSPECTION_RESULT_LABEL) as PermitInspection["result"][]).map((r) => <option key={r} value={r}>{INSPECTION_RESULT_LABEL[r]}</option>)}
                </select>
              )}
              {!closed && (
                <input type="date" value={i.scheduled_date ?? ""} aria-label="Inspection date"
                  onChange={(e) => updI.mutate({ id: i.id, permit_id: p.id, kind: i.kind, scheduled_date: e.target.value || null })}
                  className="rounded-md border border-hairline bg-white px-1.5 py-0.5 text-xs text-slate-600" />
              )}
              {i.notes && <span className="text-xs text-slate-400">{i.notes}</span>}
              {!closed && (
                <button type="button" onClick={() => delI.mutate(i.id)} title="Remove inspection" className="ml-auto rounded p-0.5 text-slate-300 opacity-0 transition-all hover:text-rose-500 group-hover:opacity-100">
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
          {!closed && p.status !== "cancelled" && (addInsp
            ? <AddInspection permitId={p.id} jobId={jobId} existing={inspections} onDone={() => setAddInsp(false)} />
            : (
              <button type="button" onClick={() => setAddInsp(true)} className="mt-0.5 inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:underline">
                <Plus className="h-3 w-3" /> Add an inspection
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

function AddInspection({ permitId, jobId, existing, onDone }: { permitId: string; jobId: string; existing: PermitInspection[]; onDone: () => void }) {
  const add = useAddInspection();
  // Rough-in first on a fresh permit, then final.
  const [kind, setKind] = useState<PermitInspection["kind"]>(existing.some((i) => i.kind === "rough_in") ? "final" : existing.length ? "final" : "rough_in");
  const [date, setDate] = useState("");
  const [result, setResult] = useState<PermitInspection["result"]>("scheduled");
  return (
    <form
      className="mt-1 flex flex-wrap items-center gap-2 rounded-md bg-white p-2 ring-1 ring-inset ring-slate-200"
      onSubmit={(e) => {
        e.preventDefault();
        add.mutate({ permit_id: permitId, job_id: jobId, kind, scheduled_date: date || null, result: date ? result : result === "scheduled" ? "needed" : result }, { onSuccess: onDone });
      }}
    >
      <select value={kind} onChange={(e) => setKind(e.target.value as PermitInspection["kind"])} className={selCls} aria-label="Inspection kind">
        {(Object.keys(INSPECTION_KIND_LABEL) as PermitInspection["kind"][]).map((k) => <option key={k} value={k}>{INSPECTION_KIND_LABEL[k]}</option>)}
      </select>
      <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-md border border-hairline bg-white px-1.5 py-1 text-xs text-slate-600" aria-label="Inspection date" />
      <select value={result} onChange={(e) => setResult(e.target.value as PermitInspection["result"])} className={selCls} aria-label="Inspection result">
        {(Object.keys(INSPECTION_RESULT_LABEL) as PermitInspection["result"][]).map((r) => <option key={r} value={r}>{INSPECTION_RESULT_LABEL[r]}</option>)}
      </select>
      <Button type="submit" size="sm" disabled={add.isPending}><CalendarClock className="h-3.5 w-3.5" /> Add</Button>
      <button type="button" onClick={onDone} className="p-1 text-slate-400 hover:text-slate-600"><X className="h-3.5 w-3.5" /></button>
      {add.isError && <span className="text-[11px] text-rose-600">Couldn&apos;t save.</span>}
    </form>
  );
}

/* -------------------------------------------------------------- editing */

function PermitEditForm({ p, job, presetStatus, initial = null, onDone }: { p: JobPermit; job?: Job; presetStatus?: PermitStatus; initial?: PermitDraft | null; onDone: () => void }) {
  const upd = useUpdatePermit();
  const { data: jurisdictions } = useJurisdictions();
  const types = usePermitTypes();
  const [trade, setTrade] = useState(initial?.trade ?? p.trade ?? "Mechanical");
  const [no, setNo] = useState(initial?.permit_no ?? p.permit_no ?? "");
  const [jur, setJur] = useState(initial?.jurisdiction ?? p.jurisdiction ?? job?.service_city ?? "");
  const [fee, setFee] = useState(initial?.fee != null ? String(initial.fee) : p.fee != null ? String(p.fee) : "");
  const [status, setStatus] = useState<PermitStatus>(presetStatus ?? p.status);
  const [notes, setNotes] = useState(initial?.notes ?? p.notes ?? "");
  return (
    <form
      className="mt-2 space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        upd.mutate({
          id: p.id, trade: trade.trim() || null, permit_no: no.trim() || null, jurisdiction: jur.trim() || null,
          fee: fee === "" ? null : Number(fee), status, prevStatus: p.status, notes: notes.trim() || null,
        }, { onSuccess: onDone });
      }}
    >
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        <input value={trade} onChange={(e) => setTrade(e.target.value)} list="pg-permit-types" placeholder="Type" className={fieldCls} aria-label="Permit type" />
        <input value={no} onChange={(e) => setNo(e.target.value)} placeholder="Permit #" className={fieldCls} autoFocus />
        <input value={jur} onChange={(e) => setJur(e.target.value)} placeholder="City / jurisdiction" list="pg-jurisdictions-edit" className={fieldCls} />
        <div className="relative">
          <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
          <input type="number" value={fee} onChange={(e) => setFee(e.target.value)} placeholder="Fee" className={cn(fieldCls, "pl-6 text-right tabular-nums")} disabled={p.by_builder} />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value as PermitStatus)} className={fieldCls} aria-label="Status">
          {PERMIT_STATUSES.map((s) => <option key={s} value={s}>{PERMIT_STATUS_LABEL[s]}</option>)}
        </select>
      </div>
      <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional) — e.g. inspector name, portal login hint" className={fieldCls} />
      {initial && <p className="flex items-center gap-1.5 text-xs font-medium text-violet-700"><Sparkles className="h-3.5 w-3.5" /> Filled in from the permit paperwork — check it, then Save.</p>}
      <datalist id="pg-permit-types">{types.map((t) => <option key={t.label} value={t.label} />)}</datalist>
      <datalist id="pg-jurisdictions-edit">{(jurisdictions ?? []).map((j) => <option key={j.id} value={j.name} />)}</datalist>
      <div className="flex items-center justify-end gap-2">
        {upd.isError && <span className="text-[11px] text-rose-600">Couldn&apos;t save — try again.</span>}
        <Button type="button" variant="ghost" size="sm" onClick={onDone}>Cancel</Button>
        <Button type="submit" size="sm" disabled={upd.isPending}><Check className="h-3.5 w-3.5" /> {upd.isPending ? "Saving…" : "Save"}</Button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------- the add */

function AddPermitForm({ job, builder, permits, types, initial = null, onAdded }: {
  job: Job; builder: boolean; permits: JobPermit[]; types: PermitType[];
  /** FILES-READ-02: read off the permit paperwork — check it, then Attach. */
  initial?: PermitDraft | null;
  onAdded?: () => void;
}) {
  const add = useAddPermit();
  const { data: jurisdictions } = useJurisdictions();
  const [trade, setTrade] = useState(initial?.trade ?? types[0]?.label ?? "Mechanical");
  const [no, setNo] = useState(initial?.permit_no ?? "");
  const [jur, setJur] = useState(initial?.jurisdiction ?? "");
  const [jurEdited, setJurEdited] = useState(!!initial?.jurisdiction);
  const [fee, setFee] = useState(initial?.fee != null ? String(initial.fee) : "");
  const [notes] = useState(initial?.notes ?? null);
  const [status, setStatus] = useState<PermitStatus>(initial?.status ?? (builder ? "issued" : "applied"));
  const firstBuilder = useRef(builder);
  useEffect(() => {
    if (firstBuilder.current === builder) return;
    firstBuilder.current = builder;
    setStatus(builder ? "issued" : "applied");
  }, [builder]);

  // Jurisdiction defaults to the service city (Seth 2026-07-07), canonicalized
  // to the matching jurisdiction name; once the office types, we stop.
  const defaultJur = useMemo(() => {
    const city = (job.service_city ?? "").trim();
    if (!city) return "";
    const hit = (jurisdictions ?? []).find((j) => j.name.toLowerCase() === city.toLowerCase());
    return hit ? hit.name : city;
  }, [job.service_city, jurisdictions]);
  useEffect(() => { if (!jurEdited) setJur(defaultJur); }, [defaultJur, jurEdited]);

  const attached = permits.filter((p) => p.status !== "cancelled").map((p) => (p.trade ?? "").toLowerCase());
  const suggest = builder || isNCJob(job) ? [] : types.filter((t) => t.suggest && !attached.includes(t.label.toLowerCase()));

  const submit = () => add.mutate(
    {
      job_id: job.id, permit_no: no.trim() || null, jurisdiction: jur.trim() || null, trade,
      fee: builder || fee === "" ? null : Number(fee), status, by_builder: builder, ...(notes ? { notes } : {}),
    },
    { onSuccess: () => { setNo(""); setJurEdited(false); setFee(""); onAdded?.(); } },
  );

  return (
    <>
      {suggest.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-brand-200 bg-brand-50/40 px-3 py-2">
          <span className="text-[11px] font-medium text-slate-500">Installs usually need:</span>
          {suggest.map((t) => (
            <button key={t.label} type="button" onClick={() => { setTrade(t.label); setJurEdited(false); }}
              title={`Fill the form with a ${t.label} permit — then add the permit # and fee`}
              className="inline-flex items-center gap-1 rounded-full border border-brand-200 bg-white px-2.5 py-1 text-xs font-medium text-brand-700 transition-colors hover:bg-brand-100">
              <Plus className="h-3 w-3" /> {permitLabel(t.label)}
            </button>
          ))}
        </div>
      )}
      <div className="mt-3 rounded-lg border border-hairline bg-slate-50/40 p-3">
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          {builder ? "Record the builder's permit (optional)" : permits.length ? "Add another permit" : "Add a permit"}
        </div>
        <div className={cn("grid grid-cols-2 gap-2", builder ? "sm:grid-cols-4" : "sm:grid-cols-5")}>
          <input value={trade} onChange={(e) => setTrade(e.target.value)} list="pg-permit-types-add" placeholder="Mechanical" className={fieldCls} aria-label="Permit type — pick or type your own" />
          <datalist id="pg-permit-types-add">{types.map((t) => <option key={t.label} value={t.label} />)}</datalist>
          <input value={no} onChange={(e) => setNo(e.target.value)} placeholder="Permit # (optional)" className={fieldCls} />
          <input value={jur} onChange={(e) => { setJur(e.target.value); setJurEdited(true); }} placeholder="Jurisdiction" list="pg-jurisdictions" className={fieldCls} />
          <datalist id="pg-jurisdictions">{(jurisdictions ?? []).map((j) => <option key={j.id} value={j.name} />)}</datalist>
          {!builder && (
            <div className="relative">
              <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
              <input type="number" value={fee} onChange={(e) => setFee(e.target.value)} placeholder="Fee" className={cn(fieldCls, "pl-6 text-right tabular-nums")} />
            </div>
          )}
          <select value={status} onChange={(e) => setStatus(e.target.value as PermitStatus)} className={fieldCls} aria-label="Status">
            {PERMIT_STATUSES.filter((s) => s !== "cancelled").map((s) => <option key={s} value={s}>{PERMIT_STATUS_LABEL[s]}</option>)}
          </select>
        </div>
        {status === "requested" && !builder && (
          <p className="mt-1.5 flex items-center gap-1 text-[11px] text-amber-700"><AlertCircle className="h-3 w-3" /> Not pulled yet — it stays in the permit queue until Applied.</p>
        )}
        {initial && (
          <p className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-violet-700"><Sparkles className="h-3.5 w-3.5" /> Filled in from the permit paperwork{notes ? ` (${notes})` : ""} — check it, then Attach Permit.</p>
        )}
        <div className="mt-2 flex items-center justify-end gap-2">
          {add.isError && <span className="text-[11px] text-rose-600">Couldn&apos;t save — try again.</span>}
          <Button variant="secondary" size="sm" disabled={(!no.trim() && !jur.trim() && status !== "requested") || add.isPending} onClick={submit}>
            <Plus className="h-3.5 w-3.5" /> {builder ? "Record Builder's Permit" : "Attach Permit"}
          </Button>
        </div>
      </div>
    </>
  );
}
